import React, { useEffect, useState, useRef } from 'react';
import { MapPin, Home } from 'lucide-react';

const Portfolio = () => {
  const [visibleCards, setVisibleCards] = useState([]);
  const sectionRef = useRef(null);

  const projects = [
    {
      title: 'Silverwoods',
      location: 'Tapkir Chowk, Rahatani, Pune',
      type: 'Residential Complex',
      description: 'Premium residential development featuring modern architecture with white and grey facade, curved balconies, and contemporary design elements.',
      image: 'https://customer-assets.emergentagent.com/job_builder-solutions-1/artifacts/2j5dn2hh_Silverwoods%20elevation.png',
      stats: [
        { label: 'Units', value: '84' },
        { label: 'Floors', value: '7' },
        { label: 'Wings', value: '2' },
        { label: 'Status', value: 'Ongoing' }
      ]
    },
    {
      title: 'Aurelia',
      location: 'Pune, Maharashtra',
      type: 'Luxury Apartments',
      description: 'Contemporary luxury residential project with elegant elevation design, combining functionality with aesthetic appeal.',
      image: 'https://customer-assets.emergentagent.com/job_builder-solutions-1/artifacts/ly44vmi3_Aurelia%20Elevation.png',
      stats: [
        { label: 'Units', value: '28' },
        { label: 'Floors', value: '7' },
        { label: 'Status', value: 'Ongoing' }
      ]
    },
    {
      title: 'Sneha Avenue',
      location: 'Pune, Maharashtra',
      type: 'Residential Development',
      description: 'Modern residential complex designed with optimal space planning and contemporary amenities for comfortable living.',
      image: 'https://customer-assets.emergentagent.com/job_builder-solutions-1/artifacts/ej10k4b3_sneha%20avenue.png',
      stats: [
        { label: 'Units', value: '28' },
        { label: 'Floors', value: '4' },
        { label: 'Status', value: 'Ongoing' }
      ]
    },
    {
      title: 'Coming Soon',
      location: 'Pune, Maharashtra',
      type: 'Upcoming Launch',
      description: 'A landmark residential tower redefining premium living — details dropping soon.',
      image: 'https://customer-assets.emergentagent.com/job_1904ef46-8f60-477e-9298-8bf3fc7cf570/artifacts/4ng07lhj_ChatGPT%20Image%20Mar%2019%2C%202026%2C%2003_08_39%20PM.png',
      comingSoon: true,
      teaser: '23 Floors \u2022 192 Premium Residences \u2022 Modern Amenities',
      stats: []
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            [0, 1, 2, 3].forEach((index) => {
              setTimeout(() => {
                setVisibleCards((prev) => [...prev, index]);
              }, index * 200);
            });
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section ref={sectionRef} id="portfolio" className="py-32 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden" data-testid="portfolio-section">
      {/* Background decoration */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-1 bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
      
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-20 scroll-reveal">
          <div className="inline-block mb-6">
            <span className="text-[13px] font-semibold tracking-[0.15em] uppercase text-gray-500 bg-gray-100 px-6 py-2 rounded-full">Our Portfolio</span>
          </div>
          <h2 className="text-5xl lg:text-[3.5rem] font-bold text-gray-900 mb-6 leading-[1.1] tracking-tight">
            Featured <span className="gradient-text">Projects</span>
          </h2>
          <p className="text-xl lg:text-2xl text-gray-600 max-w-3xl mx-auto font-light leading-[1.7] tracking-tight">
            Showcasing our successful partnerships with leading builders
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-10">
          {projects.map((project, index) => (
            <div
              key={index}
              className={`property-card group bg-white rounded-3xl overflow-hidden shadow-xl border border-gray-100 transition-all duration-700 ${
                visibleCards.includes(index) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
              }`}
              data-testid={`portfolio-card-${index}`}
            >
              {/* Project Image with optimized zoom */}
              <div className="relative h-96 overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
                {project.image ? (
                  <>
                    <img 
                      src={project.image} 
                      alt={project.title}
                      className="property-image w-full h-full object-cover object-center gpu-accelerated"
                      style={{ objectFit: 'cover' }}
                    />
                    {/* Simplified dark overlay on hover */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400"></div>
                  </>
                ) : (
                  <div className="h-full flex items-center justify-center">
                    <Home className="w-20 h-20 text-gray-400" />
                  </div>
                )}
                
                {/* Premium status badge */}
                <div className="absolute top-6 right-6 z-10">
                  <span className={`px-5 py-2 rounded-full text-xs font-semibold tracking-wide shadow-xl border group-hover:scale-105 transition-transform duration-300 ${
                    project.comingSoon
                      ? 'bg-white/90 text-black border-white/20'
                      : 'bg-black/85 text-white border-white/10'
                  }`}>
                    {project.type}
                  </span>
                </div>

                {/* Simplified hover overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400 flex items-end p-8">
                  <p className="text-white text-sm font-light opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">
                    {project.comingSoon ? 'Stay Tuned' : 'View Details →'}
                  </p>
                </div>
              </div>

              {/* Project Details */}
              <div className="p-8 relative">
                <div className="mb-6">
                  <h3 className="text-3xl font-bold text-gray-900 mb-3 group-hover:text-black transition-colors duration-300 leading-tight tracking-tight">{project.title}</h3>
                  
                  <div className="flex items-center space-x-2 text-gray-500 mb-4">
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span className="text-[15px] font-medium tracking-wide">{project.location}</span>
                  </div>
                </div>

                <p className="text-gray-600 mb-4 leading-[1.7] font-light text-[17px] tracking-tight">{project.description}</p>

                {/* Teaser line for Coming Soon */}
                {project.teaser && (
                  <p className="text-[13px] text-gray-400 font-light tracking-wide mb-6" data-testid="coming-soon-teaser">
                    {project.teaser}
                  </p>
                )}
                
                {/* Clean Premium Stats */}
                {project.stats.length > 0 && (
                  <div className="pt-6 border-t border-gray-200 mt-4">
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                      {project.stats.map((stat, idx) => (
                        <div key={idx} className="flex items-center space-x-2">
                          <span className="text-gray-400 text-sm">&bull;</span>
                          <div className="flex items-baseline space-x-1.5">
                            <span className="text-xl font-bold text-black tracking-tight">{stat.value}</span>
                            <span className="text-[15px] text-gray-600 font-medium tracking-wide">{stat.label}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Premium CTA with cinematic button */}
        <div className="mt-20 text-center scroll-reveal">
          <div className="inline-block p-10 bg-gradient-to-br from-gray-50 to-white rounded-3xl shadow-2xl border border-gray-100">
            <p className="text-xl text-gray-700 mb-6 font-light">Want to see more of our work?</p>
            <button
              onClick={() => {
                const element = document.querySelector('#contact');
                if (element) {
                  const offset = 100;
                  const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                  window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
                }
              }}
              className="inline-flex items-center space-x-3 bg-black text-white px-12 py-6 rounded-xl hover:bg-gray-900 transition-all duration-300 btn-cinematic font-semibold text-lg shadow-xl"
              data-testid="portfolio-cta-button"
            >
              <span>Request Portfolio Details</span>
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes shine {
          to {
            transform: translateX(100%);
          }
        }
      `}</style>
    </section>
  );
};

export default Portfolio;
